import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Room, Etage } from '../types';

interface FloorData {
  selectedRooms: string[];
  roomDetails: Record<string, { customName?: string; notes?: string }>;
  items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[];
}

interface CostSummaryProps {
  floorSelections: Record<string, FloorData>;
  rooms: Room[];
  etages: Etage[];
}

const CostSummary: React.FC<CostSummaryProps> = ({ floorSelections, rooms, etages }) => {
  const theme = useTheme();

  // Add more detailed debug logging
  console.log('CostSummary Input:', {
    floorSelections,
    rooms,
    etages
  });

  // Log each floor's data structure
  Object.entries(floorSelections).forEach(([floorId, data]) => {
    console.log(`Floor ${floorId} data:`, {
      selectedRooms: data.selectedRooms,
      roomDetails: data.roomDetails,
      items: data.items
    });
  });

  const getFloorName = (floorId: string) => {
    const etage = etages.find(e => e.id === floorId);
    return etage ? etage.name : floorId;
  };

  // Calculate total
  const calculateTotal = () => {
    let total = 0;
    const allItems = Object.values(floorSelections).flatMap(floor => floor.items);
    
    // Debug log items
    console.log('All Items:', allItems);
    
    allItems.forEach(item => {
      const quantity = item.quantity || 0;
      const unitPrice = item.unitPrice || 0;
      total += quantity * unitPrice;
    });

    console.log('Calculated Total:', total);
    return total;
  };

  const total = calculateTotal();

  return (
    <Box sx={{ mb: 4 }} className="cost-summary">
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 600,
          mb: 3,
          color: theme.palette.primary.main,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          pb: 1
        }}
      >
        Résumé des coûts
      </Typography>
      <TableContainer sx={{ 
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'white'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: 'white'
              }}>Étage / Pièce</TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: 'white'
              }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(floorSelections)
              .filter(([_, data]) => {
                const floorTotal = data.items.reduce((sum, item) => {
                  const quantity = item.quantity || 0;
                  const unitPrice = item.unitPrice || 0;
                  return sum + (quantity * unitPrice);
                }, 0);
                return floorTotal > 0;
              })
              .map(([floorId, data]) => {
                const floorTotal = data.items.reduce((sum, item) => {
                  const quantity = item.quantity || 0;
                  const unitPrice = item.unitPrice || 0;
                  return sum + (quantity * unitPrice);
                }, 0);

                return (
                  <React.Fragment key={floorId}>
                    {/* Floor Row */}
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: theme.palette.grey[100],
                          pl: 2
                        }}
                      >
                        {getFloorName(floorId)}
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: theme.palette.grey[100]
                        }}
                      >
                        ${floorTotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {/* Room Rows */}
                    {data.selectedRooms
                      .filter(roomId => {
                        const roomItems = data.items.filter(item => item.roomId === roomId);
                        const roomTotal = roomItems.reduce((sum, item) => {
                          const quantity = item.quantity || 0;
                          const unitPrice = item.unitPrice || 0;
                          return sum + (quantity * unitPrice);
                        }, 0);
                        return roomTotal > 0;
                      })
                      .map((roomId) => {
                        const room = rooms.find(r => r.id === roomId);
                        const roomDetail = data.roomDetails[roomId];
                        const roomItems = data.items.filter(item => item.roomId === roomId);
                        const roomTotal = roomItems.reduce((sum, item) => {
                          const quantity = item.quantity || 0;
                          const unitPrice = item.unitPrice || 0;
                          return sum + (quantity * unitPrice);
                        }, 0);

                        return (
                          <TableRow key={roomId}>
                            <TableCell sx={{ pl: 4 }}>
                              {roomDetail?.customName || room?.name}
                            </TableCell>
                            <TableCell align="right">
                              ${roomTotal.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            {/* Grand Total */}
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                }}
              >
                Total Général
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                }}
              >
                ${total.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CostSummary; 